import { INITIAL } from '@web-platform/shared-universal-constants/src/network';
import isInteger from 'lodash/isInteger';

import type { NextRequestAt } from '@web-platform/shared-universal-types/src/redux/actions/nextRequestAt';
import type { QueryItem } from '@web-platform/shared-universal-types/src/redux/state/network/queries';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

const valueIsInteger = (val: unknown): val is number => isInteger(val);

function isFutureNra(nra: NextRequestAt, now: number): nra is number {
  // If it's not a number, it can't be at a specified point in the future:
  if (!valueIsInteger(nra)) return false;
  // If it's in the past, it's not in the future:
  if (nra <= now) return false;
  // It's in the future (a number that's in the future)...
  return true;
}

function isImmediateNra(nra: NextRequestAt, now: number) {
  if (valueIsInteger(nra) && nra > now) return false;
  // It's immediate (INITIAL, or client-side a number that's in the past)...
  return true;
}

function isLegitimateNra(queryItem: QueryItem, nra: NextRequestAt) {
  // Valid type checks:
  if (nra !== INITIAL && !valueIsInteger(nra)) return false;
  // We only allow INITIAL once per query:
  if (nra === INITIAL && queryItem.lastRequestAt) return false;
  // Sanity check; a zero integer is not expected:
  if (nra === 0) return false;
  // We don't allow an epoch time that predates the last time this query was requested
  if (queryItem.lastRequestAt && valueIsInteger(nra) && nra <= queryItem.lastRequestAt)
    return false;
  // It passed all the checks!
  return true;
}

export function getHasImmediateNra(queryItem: QueryItem, now: number): boolean {
  return !!queryItem.nextRequestAt.filter(
    (nra: NextRequestAt) => isLegitimateNra(queryItem, nra) && isImmediateNra(nra, now),
  ).length;
}
export function getMinFutureNra(queryItem: QueryItem, now: number): Nullable<number> {
  const numericNras = queryItem.nextRequestAt.reduce<number[]>((nras, nra) => {
    if (isLegitimateNra(queryItem, nra) && isFutureNra(nra, now)) {
      return [...nras, nra];
    }
    return nras;
  }, []);

  return numericNras.length ? Math.min(...numericNras) : null;
}
